<template>
  <div class="tech-support-management">
    <h2>技术支持管理</h2>

    <!-- 资源下载管理 -->
    <div class="section">
      <h3>资源下载管理</h3>
      <button @click="showResourceForm = true; resetResourceForm()" class="btn-add">添加资源</button>
      <div class="search-bar">
        <input
          type="text"
          v-model="resourceSearchQuery"
          placeholder="搜索资源..."
          @input="filterResources"
        >
      </div>
      <table class="resource-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>名称</th>
            <th>类型</th>
            <th>描述</th>
            <th>大小</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="resource in filteredResources" :key="resource.id">
            <td>{{ resource.id }}</td>
            <td>{{ resource.name }}</td>
            <td>{{ resource.type === 'software' ? '软件' : '说明书' }}</td>
            <td>{{ resource.description.substring(0, 30) }}...</td>
            <td>{{ resource.size }}</td>
            <td>
              <button @click="editResource(resource)" class="btn-edit">编辑</button>
              <button @click="handleDeleteResource(resource.id)" class="btn-delete">删除</button>
            </td>
          </tr>
          <tr v-if="filteredResources.length === 0">
            <td colspan="6" class="no-data">没有找到匹配的资源</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 常见问题管理 -->
    <div class="section">
      <h3>常见问题管理</h3>
      <button @click="showFaqForm = true; resetFaqForm()" class="btn-add">添加问题</button>
      <div class="search-bar">
        <input
          type="text"
          v-model="faqSearchQuery"
          placeholder="搜索问题..."
          @input="filterFaqs"
        >
      </div>
      <table class="faq-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>问题</th>
            <th>回答</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="faq in filteredFaqs" :key="faq.id">
            <td>{{ faq.id }}</td>
            <td>{{ faq.question }}</td>
            <td>{{ faq.answer.substring(0, 50) }}...</td>
            <td>
              <button @click="editFaq(faq)" class="btn-edit">编辑</button>
              <button @click="handleDeleteFaq(faq.id)" class="btn-delete">删除</button>
            </td>
          </tr>
          <tr v-if="filteredFaqs.length === 0">
            <td colspan="4" class="no-data">没有找到匹配的问题</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 资源表单弹窗 -->
    <div v-if="showResourceForm" class="modal-overlay">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ editingResource ? '编辑资源' : '添加资源' }}</h3>
          <button @click="showResourceForm = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveResource">
            <div class="form-group">
              <label for="resourceName">资源名称（中文） *</label>
              <input
                type="text"
                id="resourceName"
                v-model="resourceForm.name"
                required
              >
              <div v-if="errors.name" class="error-message">{{ errors.name }}</div>
            </div>

            <div class="form-group">
              <label for="resourceNameEn">资源名称（英文）</label>
              <input
                type="text"
                id="resourceNameEn"
                v-model="resourceForm.nameEn"
                placeholder="Resource Name (English)"
              >
            </div>

            <div class="form-group">
              <label for="resourceType">资源类型 *</label>
              <select
                id="resourceType"
                v-model="resourceForm.type"
                required
              >
                <option value="software">软件</option>
                <option value="manual">说明书</option>
              </select>
            </div>

            <div class="form-group">
              <label for="resourceDescription">资源描述（中文） *</label>
              <textarea
                id="resourceDescription"
                v-model="resourceForm.description"
                rows="4"
                required
              ></textarea>
              <div v-if="errors.description" class="error-message">{{ errors.description }}</div>
            </div>

            <div class="form-group">
              <label for="resourceDescriptionEn">资源描述（英文）</label>
              <textarea
                id="resourceDescriptionEn"
                v-model="resourceForm.descriptionEn"
                rows="4"
                placeholder="Resource Description (English)"
              ></textarea>
            </div>

            <div class="form-group">
              <label for="resourceFile">上传文件 *</label>
              <input
                type="file"
                id="resourceFile"
                @change="handleFileUpload"
                :required="!editingResource"
              >
              <div v-if="resourceForm.fileName" class="file-name">{{ resourceForm.fileName }}</div>
              <div v-if="errors.file" class="error-message">{{ errors.file }}</div>
            </div>

            <div class="form-group">
              <label for="resourceSize">文件大小 *</label>
              <input
                type="text"
                id="resourceSize"
                v-model="resourceForm.size"
                placeholder="例如: 23.5MB"
                required
              >
              <div v-if="errors.size" class="error-message">{{ errors.size }}</div>
            </div>

            <div class="modal-footer">
              <button type="button" @click="showResourceForm = false" class="btn-cancel">取消</button>
              <button type="submit" class="btn-save">{{ editingResource ? '更新' : '保存' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- FAQ表单弹窗 -->
    <div v-if="showFaqForm" class="modal-overlay">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ editingFaq ? '编辑问题' : '添加问题' }}</h3>
          <button @click="showFaqForm = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveFaq">
            <div class="form-group">
              <label for="faqQuestion">问题（中文） *</label>
              <input
                type="text"
                id="faqQuestion"
                v-model="faqForm.question"
                required
              >
              <div v-if="faqErrors.question" class="error-message">{{ faqErrors.question }}</div>
            </div>

            <div class="form-group">
              <label for="faqQuestionEn">问题（英文）</label>
              <input
                type="text"
                id="faqQuestionEn"
                v-model="faqForm.questionEn"
                placeholder="Question (English)"
              >
            </div>

            <div class="form-group">
              <label for="faqAnswer">回答（中文） *</label>
              <textarea
                id="faqAnswer"
                v-model="faqForm.answer"
                rows="6"
                required
              ></textarea>
              <div v-if="faqErrors.answer" class="error-message">{{ faqErrors.answer }}</div>
            </div>

            <div class="form-group">
              <label for="faqAnswerEn">回答（英文）</label>
              <textarea
                id="faqAnswerEn"
                v-model="faqForm.answerEn"
                rows="6"
                placeholder="Answer (English)"
              ></textarea>
            </div>

            <div class="modal-footer">
              <button type="button" @click="showFaqForm = false" class="btn-cancel">取消</button>
              <button type="submit" class="btn-save">{{ editingFaq ? '更新' : '保存' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 技术文档管理 -->
    <div class="section">
      <h3>技术文档管理</h3>
      <button @click="showDocumentForm = true; resetDocumentForm()" class="btn-add">添加文档</button>
      <div class="search-bar">
        <input
          type="text"
          v-model="documentSearchQuery"
          placeholder="搜索文档..."
          @input="filterDocuments"
        >
      </div>
      <table class="document-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>标题</th>
            <th>分类</th>
            <th>描述</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="document in filteredDocuments" :key="document.id">
            <td>{{ document.id }}</td>
            <td>{{ document.title }}</td>
            <td>{{ document.category }}</td>
            <td>{{ document.description.substring(0, 30) }}...</td>
            <td>
              <button @click="editDocument(document)" class="btn-edit">编辑</button>
              <button @click="handleDeleteDocument(document.id)" class="btn-delete">删除</button>
            </td>
          </tr>
          <tr v-if="filteredDocuments.length === 0">
            <td colspan="5" class="no-data">没有找到匹配的文档</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 文档表单弹窗 -->
    <div v-if="showDocumentForm" class="modal-overlay">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ editingDocument ? '编辑文档' : '添加文档' }}</h3>
          <button @click="showDocumentForm = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveDocument">
            <div class="form-group">
              <label for="documentTitle">文档标题（中文） *</label>
              <input
                type="text"
                id="documentTitle"
                v-model="documentForm.title"
                required
              >
              <div v-if="documentErrors.title" class="error-message">{{ documentErrors.title }}</div>
            </div>

            <div class="form-group">
              <label for="documentTitleEn">文档标题（英文）</label>
              <input
                type="text"
                id="documentTitleEn"
                v-model="documentForm.titleEn"
                placeholder="Document Title (English)"
              >
            </div>

            <div class="form-group">
              <label for="documentDescription">文档描述（中文） *</label>
              <textarea
                id="documentDescription"
                v-model="documentForm.description"
                rows="4"
                required
              ></textarea>
              <div v-if="documentErrors.description" class="error-message">{{ documentErrors.description }}</div>
            </div>

            <div class="form-group">
              <label for="documentDescriptionEn">文档描述（英文）</label>
              <textarea
                id="documentDescriptionEn"
                v-model="documentForm.descriptionEn"
                rows="4"
                placeholder="Document Description (English)"
              ></textarea>
            </div>

            <div class="form-group">
              <label for="documentFile">上传文件 *</label>
              <input
                type="file"
                id="documentFile"
                @change="handleDocumentFileUpload"
                :required="!editingDocument"
              >
              <div v-if="documentForm.fileName" class="file-name">{{ documentForm.fileName }}</div>
              <div v-if="documentErrors.file" class="error-message">{{ documentErrors.file }}</div>
            </div>

            <div class="modal-footer">
              <button type="button" @click="showDocumentForm = false" class="btn-cancel">取消</button>
              <button type="submit" class="btn-save">{{ editingDocument ? '更新' : '保存' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
// 导入API函数，添加文档相关API
import {
  getResources,
  getFaqs,
  createResource,
  updateResource,
  deleteResource,
  createFaq,
  updateFaq,
  deleteFaq,
  uploadFile,
  getDocuments,
  createDocument,
  updateDocument,
  deleteDocument
} from '@/api/techSupport';

const resources = ref([]);
const faqs = ref([]);
const documents = ref([]);  // 添加文档数据变量
const showResourceForm = ref(false);
const showFaqForm = ref(false);
const showDocumentForm = ref(false);  // 添加文档表单状态
const editingResource = ref(null);
const editingFaq = ref(null);
const editingDocument = ref(null);  // 添加文档编辑状态
const resourceSearchQuery = ref('');
const faqSearchQuery = ref('');
const documentSearchQuery = ref('');  // 添加文档搜索查询
const errors = ref({});
const faqErrors = ref({});
const documentErrors = ref({});  // 添加文档错误状态

// 资源表单数据
const resourceForm = ref({
  id: null,
  name: '',
  nameEn: '',
  type: 'software',
  description: '',
  descriptionEn: '',
  size: '',
  fileName: '',
  file: null,
  storage_path: ''
});

// FAQ表单数据
const faqForm = ref({
  id: null,
  question: '',
  questionEn: '',
  answer: '',
  answerEn: ''
});

// 文档表单数据
const documentForm = ref({
  id: null,
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  category: 'general',
  fileName: '',
  file: null,
  storage_path: ''
});

// 过滤后的资源列表
const filteredResources = computed(() => {
  if (!resourceSearchQuery.value) return resources.value;
  const query = resourceSearchQuery.value.toLowerCase();
  return resources.value.filter(resource =>
    resource.name.toLowerCase().includes(query) ||
    resource.description.toLowerCase().includes(query) ||
    resource.type.toLowerCase().includes(query)
  );
});

// 过滤后的FAQ列表
const filteredFaqs = computed(() => {
  if (!faqSearchQuery.value) return faqs.value;
  const query = faqSearchQuery.value.toLowerCase();
  return faqs.value.filter(faq =>
    faq.question.toLowerCase().includes(query) ||
    faq.answer.toLowerCase().includes(query)
  );
});

// 过滤后的文档列表
const filteredDocuments = computed(() => {
  if (!documentSearchQuery.value) return documents.value;
  const query = documentSearchQuery.value.toLowerCase();
  return documents.value.filter(document =>
    document.title.toLowerCase().includes(query) ||
    document.description.toLowerCase().includes(query) ||
    document.category.toLowerCase().includes(query)
  );
});  // 添加文档计算属性

// 加载资源数据
const loadResources = async () => {
  try {
    // 实际项目中使用API调用
    const response = await getResources();
    resources.value = response.data;

    // 删除模拟数据
    // resources.value = [
    //   {
    //     id: 1,
    //     name: '智能门机控制系统 V2.3',
    //     type: 'software',
    //     description: '适用于各类智能门控场景的控制软件',
    //     size: '23.5MB',
    //     url: '/public/assets/downloads/door_control_v2.3.exe'
    //   },
    //   // 更多模拟数据...
    // ];
  } catch (error) {
    console.error('加载资源失败:', error);
    alert('加载资源失败，请重试');
  }
};

// 加载FAQ数据
const loadFaqs = async () => {
  try {
    const response = await getFaqs();
    // 检查响应格式
    if (response.data && Array.isArray(response.data)) {
      faqs.value = response.data;
    } else if (response.data && response.data.error) {
      throw new Error(response.data.error);
    } else {
      faqs.value = [];
    }
  } catch (error) {
    console.error('加载FAQ失败:', error);
    const errorMessage = error.response?.data?.error || error.message || '加载FAQ失败，请重试';
    alert(`加载FAQ失败: ${errorMessage}`);
    
    // 如果错误提示包含字段不存在，提醒用户执行数据库迁移脚本
    if (errorMessage.includes('Unknown column') || errorMessage.includes('does not exist') || errorMessage.includes('数据库字段未添加')) {
      alert('数据库字段未添加，请先执行 fix-database.sql 脚本添加英文字段');
    }
    
    // 设置空数组避免页面报错
    faqs.value = [];
  }
};

// 加载文档数据
const loadDocuments = async () => {
  try {
    const response = await getDocuments();
    documents.value = response.data;
  } catch (error) {
    console.error('加载文档失败:', error);
    alert('加载文档失败，请重试');
  }
};  // 添加加载文档方法

// 重置资源表单
const resetResourceForm = () => {
  resourceForm.value = {
    id: null,
    name: '',
    nameEn: '',
    type: 'software',
    description: '',
    descriptionEn: '',
    size: '',
    fileName: '',
    file: null,
    storage_path: ''
  };
  editingResource.value = null;
  errors.value = {};
};

// 编辑资源
const editResource = (resource) => {
  resourceForm.value = {
    ...resource,
    nameEn: resource.nameEn || '',
    descriptionEn: resource.descriptionEn || '',
    fileName: resource.url.split('/').pop(),
    file: null,
    storage_path: resource.storage_path || ''
  };
  editingResource.value = resource.id;
  errors.value = {};
  showResourceForm.value = true;
};

// 处理文件上传
const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    // 添加文件大小检查 (100MB = 16 * 1024 * 1024 bytes)
    const MAX_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('文件大小不能超过100MB');
      event.target.value = ''; // 清空文件选择
      return;
    }
    resourceForm.value.file = file;
    resourceForm.value.fileName = file.name;
    // 自动填充文件大小
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
    resourceForm.value.size = `${sizeInMB}MB`;
  }
};

// 验证资源表单
const validateResourceForm = () => {
  let isValid = true;
  errors.value = {};

  if (!resourceForm.value.name.trim()) {
    errors.value.name = '请输入资源名称';
    isValid = false;
  }

  if (!resourceForm.value.description.trim()) {
    errors.value.description = '请输入资源描述';
    isValid = false;
  }

  if (!resourceForm.value.size.trim()) {
    errors.value.size = '请输入文件大小';
    isValid = false;
  }

  if (!editingResource.value && !resourceForm.value.file) {
    errors.value.file = '请上传文件';
    isValid = false;
  }

  return isValid;
};

// 保存资源
const saveResource = async () => {
  if (!validateResourceForm()) return;

  try {
    // 实际项目中，这里会先上传文件
    let fileUrl = resourceForm.value.url;
    let storagePath = resourceForm.value.storage_path;
    if (resourceForm.value.file) {
      const uploadResponse = await uploadFile(resourceForm.value.file);
      fileUrl = uploadResponse.data.url;
      // 获取后端返回的storage_path
      storagePath = uploadResponse.data.storage_path;
    }

    // 清理数据，移除不需要的字段
    const resourceData = {
      name: resourceForm.value.name,
      nameEn: resourceForm.value.nameEn || '',
      type: resourceForm.value.type,
      description: resourceForm.value.description,
      descriptionEn: resourceForm.value.descriptionEn || '',
      size: resourceForm.value.size,
      url: fileUrl,
      storage_path: storagePath
    };
    
    // 如果没有URL，说明是新资源但没有上传文件
    if (!fileUrl) {
      alert('请上传文件');
      return;
    }

    if (editingResource.value) {
      // 实际项目中使用API调用
      await updateResource(editingResource.value, resourceData);
      // 更新本地数据
      const index = resources.value.findIndex(r => r.id === editingResource.value);
      if (index !== -1) {
        resources.value[index] = {...resources.value[index], ...resourceData};
      }
    } else {
      // 实际项目中使用API调用
      const response = await createResource(resourceData);
      resources.value.push(response.data);
      // 删除模拟添加代码
      // const newId = Math.max(...resources.value.map(r => r.id), 0) + 1;
      // resources.value.push({
      //   id: newId,
      //   ...resourceData,
      //   url: `/public/assets/downloads/${resourceData.fileName}`
      // });
    }

    showResourceForm.value = false;
    alert('保存成功');
    await loadResources(); // 重新加载资源列表
  } catch (error) {
    console.error('保存资源失败:', error);
    const errorMessage = error.response?.data?.error || error.message || '保存资源失败，请重试';
    alert(`保存资源失败: ${errorMessage}`);
    
    // 如果错误提示包含字段不存在，提醒用户执行数据库迁移脚本
    if (errorMessage.includes('Unknown column') || errorMessage.includes('does not exist')) {
      alert('数据库字段未添加，请先执行 fix-database.sql 脚本添加英文字段');
    }
  }
};

// 删除资源
const handleDeleteResource = async (id) => {
  if (confirm('确定要删除这个资源吗？')) {
    try {
      // 实际项目中使用API调用
      await deleteResource(id);
      // 从列表中移除
      resources.value = resources.value.filter(resource => resource.id !== id);
      alert('删除成功');
    } catch (error) {
      console.error('删除资源失败:', error);
      alert('删除资源失败，请重试');
    }
  }
};

// 重置FAQ表单
const resetFaqForm = () => {
  faqForm.value = {
    id: null,
    question: '',
    questionEn: '',
    answer: '',
    answerEn: ''
  };
  editingFaq.value = null;
  faqErrors.value = {};
};

// 编辑FAQ
const editFaq = (faq) => {
  faqForm.value = {
    ...faq,
    questionEn: faq.questionEn || '',
    answerEn: faq.answerEn || ''
  };
  editingFaq.value = faq.id;
  faqErrors.value = {};
  showFaqForm.value = true;
};

// 验证FAQ表单
const validateFaqForm = () => {
  let isValid = true;
  faqErrors.value = {};

  if (!faqForm.value.question.trim()) {
    faqErrors.value.question = '请输入问题';
    isValid = false;
  }

  if (!faqForm.value.answer.trim()) {
    faqErrors.value.answer = '请输入回答';
    isValid = false;
  }

  return isValid;
};

// 保存FAQ
const saveFaq = async () => {
  if (!validateFaqForm()) return;

  try {
    if (editingFaq.value) {
      // 实际项目中使用API调用
      const response = await updateFaq(editingFaq.value, faqForm.value);
      // 更新本地数据
      const index = faqs.value.findIndex(f => f.id === editingFaq.value);
      if (index !== -1) {
        faqs.value[index] = response.data;  // 使用后端返回的数据
      }
    } else {
      const response = await createFaq(faqForm.value);
      faqs.value.push(response.data);
    }

    showFaqForm.value = false;
    alert('保存成功');
  } catch (error) {
    alert(`保存FAQ失败: ${error.message}`);
  }
};

// 删除FAQ
const handleDeleteFaq = async (id) => {
  if (confirm('确定要删除这个问题吗？')) {
    try {
      // 实际项目中使用API调用
      await deleteFaq(id);
      // 从列表中移除
      faqs.value = faqs.value.filter(faq => faq.id !== id);
      alert('删除成功');
    } catch (error) {
      console.error('删除FAQ失败:', error);
      alert('删除FAQ失败，请重试');
    }
  }
};

// 重置文档表单
const resetDocumentForm = () => {
  documentForm.value = {
    id: null,
    title: '',
    titleEn: '',
    description: '',
    descriptionEn: '',
    category: 'general',
    fileName: '',
    file: null,
    storage_path: ''
  };
  editingDocument.value = null;
  documentErrors.value = {};
};

// 编辑文档
const editDocument = (document) => {
  documentForm.value = {
    ...document,
    titleEn: document.titleEn || '',
    descriptionEn: document.descriptionEn || '',
    fileName: document.url.split('/').pop(),
    file: null,
    storage_path: document.storage_path || ''
  };
  editingDocument.value = document.id;
  documentErrors.value = {};
  showDocumentForm.value = true;
};

// 处理文档文件上传
const handleDocumentFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    documentForm.value.file = file;
    documentForm.value.fileName = file.name;
  }
};

// 验证文档表单
const validateDocumentForm = () => {
  let isValid = true;
  documentErrors.value = {};

  if (!documentForm.value.title.trim()) {
    documentErrors.value.title = '请输入文档标题';
    isValid = false;
  }

  if (!documentForm.value.description.trim()) {
    documentErrors.value.description = '请输入文档描述';
    isValid = false;
  }

  if (!editingDocument.value && !documentForm.value.file) {
    documentErrors.value.file = '请上传文件';
    isValid = false;
  }

  return isValid;
};

// 保存文档
const saveDocument = async () => {
  if (!validateDocumentForm()) return;

  try {
    let fileUrl = documentForm.value.url;
    let storagePath = documentForm.value.storage_path;
    if (documentForm.value.file) {
      const uploadResponse = await uploadFile(documentForm.value.file);
      fileUrl = uploadResponse.data.url;
      // 获取后端返回的storage_path
      storagePath = uploadResponse.data.storage_path;
    }

    const documentData = {
      ...documentForm.value,
      url: fileUrl,
      storage_path: storagePath,
      created_at: new Date().toISOString().split('T')[0]
    };

    if (editingDocument.value) {
      await updateDocument(editingDocument.value, documentData);
      const index = documents.value.findIndex(d => d.id === editingDocument.value);
      if (index !== -1) {
        documents.value[index] = {...documents.value[index], ...documentData};
      }
    } else {
      const response = await createDocument(documentData);
      documents.value.push(response.data);
    }

    showDocumentForm.value = false;
    alert('保存成功');
  } catch (error) {
    console.error('保存文档失败:', error);
    alert('保存文档失败，请重试');
  }
};

// 删除文档
const handleDeleteDocument = async (id) => {
  if (confirm('确定要删除这个文档吗？')) {
    try {
      await deleteDocument(id);
      documents.value = documents.value.filter(document => document.id !== id);
      alert('删除成功');
    } catch (error) {
      console.error('删除文档失败:', error);
      alert('删除文档失败，请重试');
    }
  }
};

// 页面加载时获取数据
onMounted(() => {
  loadResources();
  loadFaqs();
  loadDocuments();  // 添加加载文档
});
</script>

<style scoped lang="scss">
@import '@/styles/sections/admin/techsupport/techsupportmanage';

</style>
